import React, { useState, useEffect, useContext, useCallback, useRef } from 'react'
import styled, { createGlobalStyle } from 'styled-components'
import { useQuery } from '@apollo/client'
import UserContext from './context/UserContext.js'
import { DialogContext } from './context/DialogContext.js'
import { GET_INSPIRATIONS_QUERY, GET_USER_PROFILE_QUERY, ME_QUERY } from './query/gqlQueries.js'
import { mapInspirationData } from './query/queryMappers.js'
import { isTeamRole, isOwnerRole } from './util/roles.js'
import { completeSpotifyLogin } from './spotify/spotify.js'
import SongView from './UI/SongView.js'
import { setMeetingDocPrefill } from './dialog/AddMeetingDocumentDialog.js'
import MusicNoteIcon from '@mui/icons-material/MusicNote'
import { setNativeLanguages } from './speech/flowMatch.js'
import { base } from './hudStyles.js'

import Hud from './Hud.js'
import HomeView from './UI/HomeView.js'
import InspirationFullView from './UI/InspirationFullView.js'
import KanbanView from './UI/KanBanView.js'
import TaskBoardView from './UI/TaskBoardView.js'
import SpeechFullView from './UI/SpeechFullView.js'
import SpeechFullViewPractice from './UI/SpeechFullViewPractice.js'
import LanguagePartnerView from './UI/LanguagePartnerView.js'
import FlashcardView from './UI/FlashcardView.js'
import MeetingView from './UI/MeetingView.js'
import ParagraphReaderView from './UI/ParagraphReaderView.js'
import MediaView from './UI/MediaView.js'
import JournalView from './UI/JournalView.js'
import ManyTranslationsFullView from './UI/ManyTranslationsFullView.js'
import TranslationFullView from './UI/TranslationFullView.js'
import VerbFlowView from './UI/VerbFlowView.js'
import SayItView from './UI/SayItView.js'
import SpecialModeView from './UI/SpecialModeView.js'
import LanguageTreeView from './UI/LanguageTreeView.js'

import EditTaskDialog from './UI/EditTaskDialog.js'
import AddSentenceDialog from './dialog/AddSentenceDialog.js'
import AddSentenceAllTranslationsDialog from './dialog/AddSentenceAllTranslationsDialog.js'
import AddSentencesExcelDialog from './dialog/AddSentencesExcelDialog.js'
import AddMeetingDocumentDialog from './dialog/AddMeetingDocumentDialog.js'
import AddLessonFromQueryDialog from './dialog/AddLessonFromQueryDialog.js'
import ImportLessonDialog from './dialog/ImportLessonDialog.js'
import ProfileDialog, { markProfileOnboarding } from './dialog/ProfileDialog.js'
import ReviewDueDialog from './dialog/ReviewDueDialog.js'
import IntroduceYourselfDialog from './dialog/IntroduceYourselfDialog.js'
import HomeworkDialog from './dialog/HomeworkDialog.js'
import AddJournalDialog from './dialog/AddJournalDialog.js'
import AddParagraphDialog from './dialog/AddParagraphDialog.js'
import AddWordDialog from './dialog/AddWordDialog.js'
import AddConversationDialog from './dialog/AddConversationDialog.js'
import AddConsumptionDialog from './dialog/AddConsumptionDialogue.js'
import AddProjectDialog from './dialog/AddProjectDialog.js'
import AddTaskGroupDialog from './dialog/AddTaskGroupDialog.js'
import AddInspirationDialog from './dialog/AddInspirationDialog.js'
import AddNoteDialog from './dialog/AddNoteDialog.js'
import NotesListDialog from './dialog/NotesListDialog.js'

import CloseIcon from '@mui/icons-material/Close'
import AbcIcon from '@mui/icons-material/Abc'
import TranslateIcon from '@mui/icons-material/Translate'
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver'
import ChatIcon from '@mui/icons-material/Chat'
import StyleIcon from '@mui/icons-material/Style'
import EventNoteIcon from '@mui/icons-material/EventNote'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import CloudIcon from '@mui/icons-material/Cloud'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import ScienceIcon from '@mui/icons-material/Science'
import SpellcheckIcon from '@mui/icons-material/Spellcheck'
import ReplayIcon from '@mui/icons-material/Replay'
import EmojiPeopleIcon from '@mui/icons-material/EmojiPeople'
import AssignmentIcon from '@mui/icons-material/Assignment'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import SchoolIcon from '@mui/icons-material/School'
import SmsIcon from '@mui/icons-material/Sms'
import GTranslateIcon from '@mui/icons-material/GTranslate'
import QueueIcon from '@mui/icons-material/Queue'
import EventIcon from '@mui/icons-material/Event'
import PersonIcon from '@mui/icons-material/Person'
import ViewKanbanIcon from '@mui/icons-material/ViewKanban'
import ListAltIcon from '@mui/icons-material/ListAlt'
import PermMediaIcon from '@mui/icons-material/PermMedia'
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement'
import PostAddIcon from '@mui/icons-material/PostAdd'
import TaskIcon from '@mui/icons-material/Task'
import NoteAddIcon from '@mui/icons-material/NoteAdd'
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark'
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer'
import TagIcon from '@mui/icons-material/Tag'

// ─────────────────────────────────────────────────────────────────────────────
// The app shell. Owns:
//   • which full-screen view is open (`openView`, one id or null) and the
//     wrappers that keep every view mounted so its state survives a visit;
//   • the dialog portals;
//   • the action registry — every feature the app can open, once, with its
//     group and role — which the home page, the Learn grid and the word-cloud
//     dials all render from;
//   • the two idle surfaces: the home page (default) and the word cloud with
//     its HUD chrome (Hud.js), which is now just a view of its own.
// Session plumbing (role, profile, native languages, onboarding) lives here too.
// ─────────────────────────────────────────────────────────────────────────────

// Views without a close control of their own (they used to be closed from the
// HUD fan, which isn't there when they're opened from the home page). The shell
// gives these a close button and Escape.
const BARE_VIEWS = new Set(['inspirations', 'speech', 'manyTranslations', 'translation'])

export default function AppShell({
  sourceLanguage,
  targetLanguage,
  setTargetLanguage,
  onData,
  label,
  setLabel,
  owner,
  setOwner,
  labels,
  owners,
  languageOptions,
  sentenceList,
  voiceSpeed
}) {
  const { username } = useContext(UserContext)
  const { dialogStates, openDialog, closeDialog } = useContext(DialogContext)

  // ── session ────────────────────────────────────────────────────────────────
  // Team-only entries are gated on the role stored on the User node ('owner'
  // or 'team'). Until `me` answers, isTeam is false, so students never see a
  // flash of the work tools.
  const { data: meData } = useQuery(ME_QUERY, { errorPolicy: 'ignore' })
  const isTeam = isTeamRole(meData?.me?.role)
  // Beta entries are for the owner alone while they settle.
  const isOwner = isOwnerRole(meData?.me?.role)

  const { data: profileData } = useQuery(GET_USER_PROFILE_QUERY, { variables: {}, skip: !username, errorPolicy: 'ignore' })
  // Tell the shared romanizer which languages are the learner's own, so the
  // drills and flows never romanize a mother tongue.
  useEffect(() => {
    setNativeLanguages((profileData?.getUserProfile?.native || []).map(l => l && l.code))
  }, [profileData])
  // Onboarding nudge: the first time a signed-in user has no name on their
  // profile, open the Profile dialog once. The localStorage flag keeps it from
  // re-opening on every load.
  useEffect(() => {
    if (!profileData?.getUserProfile) return
    const hasName = !!profileData.getUserProfile.user?.name
    let alreadyPrompted = false
    try { alreadyPrompted = localStorage.getItem('profilePrompted') === '1' } catch { /* ignore */ }
    if (!hasName && !alreadyPrompted) {
      try { localStorage.setItem('profilePrompted', '1') } catch { /* ignore */ }
      // Tell the dialog it opened itself, so it can say every field is
      // optional and offer a "Not now" rather than just a ✕.
      markProfileOnboarding()
      openDialog('dialogProfile')
    }
  }, [profileData, openDialog])

  // ── inspirations (rotating quote for the Inspirations view) ────────────────
  const { loading: inspirationsLoading, data: inspirationsData } = useQuery(GET_INSPIRATIONS_QUERY, {
    variables: { username },
    skip: !username
  })
  const inspirations = inspirationsData ? mapInspirationData(inspirationsData) : []
  const [currentInspirationIndex, setCurrentInspirationIndex] = useState(0)
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeout(() => {
        setCurrentInspirationIndex((prevIndex) => (prevIndex + 1 >= inspirations.length ? 0 : prevIndex + 1))
      }, 1000)
    }, 10000)
    return () => clearInterval(interval)
  }, [inspirations.length, currentInspirationIndex])
  const currentInspiration = inspirationsLoading ? <p>Loading inspirations...</p> : inspirations[currentInspirationIndex]?.source

  // ── which view is open ─────────────────────────────────────────────────────
  // One id (or null) instead of seventeen booleans. The home page is the idle
  // state; the word cloud is the other idle surface (`showHome` false).
  const [openView, setOpenView] = useState(null)
  const [showHome, setShowHome] = useState(true)
  const toggle = useCallback((id) => setOpenView(v => (v === id ? null : id)), [])
  const isOpen = (id) => openView === id
  const show = (id) => ({ display: openView === id ? 'block' : 'none' })
  // Adapter for views that still take a `setIsXVisible`-style setter. Stable
  // per id, so a view that lists the setter in effect deps doesn't re-run.
  const visRef = useRef({})
  const vis = (id) => {
    if (!visRef.current[id]) {
      visRef.current[id] = (v) => setOpenView(prev => {
        const next = typeof v === 'function' ? v(prev === id) : v
        return next ? id : (prev === id ? null : prev)
      })
    }
    return visRef.current[id]
  }

  // Cards handed to the Flashcard pipeline from a meeting, a paragraph, the
  // review dialog or "Introduce yourself".
  const [practiceFlashcards, setPracticeFlashcards] = useState(null)
  const practiceCards = (cards) => { setPracticeFlashcards(cards); setOpenView('flashcards') }
  const handleStartReview = (cards) => { closeDialog('dialogReviewDue'); practiceCards(cards) }

  // Opening the Skill Tree from the dashboard can land on a specific topic.
  const [treeFocus, setTreeFocus] = useState(null) // { language, slug }
  const openTreeAt = (language, slug) => { setTreeFocus({ language, slug }); setOpenView('tree') }
  // Review is per language; the dashboard says which one to open on.
  const [reviewLang, setReviewLang] = useState(null)
  const openReviewFor = (lang) => { setReviewLang(lang || null); openDialog('dialogReviewDue') }

  // Escape closes a bare view (one with no close control of its own), unless
  // a dialog is up — the dialog owns Escape then.
  const anyDialog = Object.values(dialogStates || {}).some(Boolean)
  useEffect(() => {
    if (!openView || !BARE_VIEWS.has(openView) || anyDialog) return
    const onKey = (e) => { if (e.key === 'Escape') setOpenView(null) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [openView, anyDialog])

  // ── the action registry ────────────────────────────────────────────────────
  // Every feature, once. `group` drives the sections on the home page and in
  // the Learn grid; `team` marks entries only owner/team accounts get.
  const dlg = (key) => () => openDialog(key)
  const view = (id) => () => toggle(id)
  const actions = [
    { id: 'partner', group: 'Views', name: 'Language Partner', icon: <ChatIcon />, onClick: view('partner') },
    { id: 'flashcards', group: 'Views', name: 'Flashcards', icon: <StyleIcon />, onClick: view('flashcards') },
    { id: 'meetings', group: 'Views', name: 'Past Lessons', icon: <EventNoteIcon />, onClick: view('meetings') },
    { id: 'paragraphs', group: 'Views', name: 'Paragraphs', icon: <MenuBookIcon />, onClick: view('paragraphs') },
    { id: 'cloud', group: 'Views', name: 'Word cloud', icon: <CloudIcon />, onClick: () => setShowHome(false) },

    { id: 'tree', group: 'Practice', name: 'Skill Tree', icon: <AccountTreeIcon />, onClick: view('tree') },
    { id: 'specialMode', group: 'Practice', name: 'Special Mode', icon: <ScienceIcon />, onClick: view('specialMode') },
    { id: 'verbLab', group: 'Practice', name: 'Verb Lab', icon: <SpellcheckIcon />, onClick: view('verbLab') },
    { id: 'sayIt', group: 'Practice', name: 'What I want to say', icon: <QuestionAnswerIcon />, onClick: view('sayIt') },
    { id: 'review', group: 'Practice', name: 'Review due', icon: <ReplayIcon />, onClick: () => openReviewFor(null) },
    { id: 'introduce', group: 'Practice', name: 'Introduce Yourself', icon: <EmojiPeopleIcon />, onClick: dlg('dialogIntroduce') },

    { id: 'homework', group: 'Lessons', name: 'Homework', icon: <AssignmentIcon />, onClick: dlg('dialogHomework') },
    { id: 'aiLesson', group: 'Lessons', name: 'AI Lesson', icon: <AutoAwesomeIcon />, onClick: dlg('dialogAddLessonFromQuery') },
    { id: 'importLesson', group: 'Lessons', name: 'Import Lesson', icon: <SchoolIcon />, onClick: dlg('dialogImportLesson') },

    { id: 'addSentence', group: 'Add material', name: 'Sentence', icon: <SmsIcon />, onClick: dlg('dialogAddSentence') },
    { id: 'addSentenceAll', group: 'Add material', name: 'Translate to all', icon: <GTranslateIcon />, onClick: dlg('dialogAddSentenceAllTranslations') },
    { id: 'addSentencesExcel', group: 'Add material', name: 'Sentences (Bulk)', icon: <QueueIcon />, onClick: dlg('dialogAddSentencesExcel') },
    { id: 'addMeetingDoc', group: 'Add material', name: 'Meeting Documents', icon: <EventIcon />, onClick: dlg('dialogAddMeetingDocument') },

    { id: 'profile', group: 'You', name: 'Profile', icon: <PersonIcon />, onClick: dlg('dialogProfile') },

    { id: 'kanban', group: 'Work', team: true, name: 'Kanban', icon: <ViewKanbanIcon />, onClick: view('kanban') },
    { id: 'taskBoard', group: 'Work', team: true, name: 'Task Board', icon: <ListAltIcon />, onClick: view('taskBoard') },
    { id: 'media', group: 'Work', team: true, name: 'Media', icon: <PermMediaIcon />, onClick: view('media') },
    { id: 'journal', group: 'Work', team: true, name: 'Journal', icon: <SelfImprovementIcon />, onClick: view('journal') },
    { id: 'addProject', group: 'Work', team: true, name: 'Project', icon: <PostAddIcon />, onClick: dlg('dialogAddProject') },
    { id: 'addTask', group: 'Work', team: true, name: 'Task', icon: <TaskIcon />, onClick: dlg('dialogAddTask') },
    { id: 'importTasks', group: 'Work', team: true, name: 'Import Tasks (Doc)', icon: <PostAddIcon />, onClick: dlg('dialogAddTaskGroup') },
    { id: 'addNote', group: 'Work', team: true, name: 'New Note', icon: <NoteAddIcon />, onClick: dlg('dialogAddNote') },
    { id: 'notes', group: 'Work', team: true, name: 'My Notes', icon: <CollectionsBookmarkIcon />, onClick: dlg('dialogNotesList') },

    { id: 'addInspiration', group: 'Save Ideas', team: true, name: 'Inspiration', icon: <QuestionAnswerIcon />, onClick: dlg('dialogAddInspiration') },
    { id: 'addConsumption', group: 'Save Ideas', team: true, name: 'Consumption', icon: <TagIcon />, onClick: dlg('dialogAddConsumption') },
    { id: 'addJournal', group: 'Save Ideas', team: true, name: 'Journal Entry', icon: <SelfImprovementIcon />, onClick: dlg('dialogAddJournal') },

    // β — owner only until it has settled. Not in the Learn grid's groups, so it
    // appears on the home page's "Everything" section alone.
    { id: 'song', group: 'β Beta', owner: true, name: 'Song mode', icon: <MusicNoteIcon />, onClick: view('song') },
    // Parked in β while they are reworked — owner only, so nobody else meets
    // them half-finished. They keep working exactly as before.
    { id: 'inspirations', group: 'β Beta', owner: true, name: 'Inspirations View', icon: <AbcIcon />, onClick: view('inspirations') },
    { id: 'translation', group: 'β Beta', owner: true, name: 'Translation View', icon: <TranslateIcon />, onClick: view('translation') },
    { id: 'speech', group: 'β Beta', owner: true, name: 'Speech Translation', icon: <RecordVoiceOverIcon />, onClick: view('speech') },
    { id: 'speechPractice', group: 'β Beta', owner: true, name: 'Speech Practice', icon: <RecordVoiceOverIcon />, onClick: view('speechPractice') },
    { id: 'manyTranslations', group: 'β Beta', owner: true, name: 'Many Translations', icon: <ChatIcon />, onClick: view('manyTranslations') },
  ].filter(a => (!a.team || isTeam) && (!a.owner || isOwner))

  // Coming back from Spotify's consent page: finish the login (the URL carries
  // ?code&state), then land straight in Song mode.
  useEffect(() => {
    completeSpotifyLogin()
      .then((ok) => { if (ok) setOpenView('song') })
      .catch((e) => console.warn('[spotify] login not completed:', e.message))
  }, [])

  return (
    <>
      {/* ── full-screen views: kept mounted, shown one at a time ── */}
      <AllView style={show('inspirations')}>
        <InspirationFullView
          currentInspirationIndex={currentInspirationIndex}
          setCurrentInspirationIndex={setCurrentInspirationIndex}
          inspirations={inspirations}
          isAllViewVisible={isOpen('inspirations')}
          setIsAllViewVisible={vis('inspirations')}
          currentInspiration={currentInspiration}
        />
      </AllView>
      <AllView style={show('kanban')}>
        <KanbanView sentenceList={sentenceList} voiceSpeed={voiceSpeed} isKanbanViewVisible={isOpen('kanban')} setIsKanbanViewVisible={vis('kanban')} username={username} />
      </AllView>
      <AllView style={show('taskBoard')}>
        <TaskBoardView isTaskBoardViewVisible={isOpen('taskBoard')} setIsTaskBoardViewVisible={vis('taskBoard')} username={username} />
      </AllView>
      <AllView style={show('speech')}>
        <SpeechFullView sentenceList={sentenceList} voiceSpeed={voiceSpeed} isAllViewSpeechVisible={isOpen('speech')} setIsAllViewSpeechVisible={vis('speech')} />
      </AllView>
      <AllView style={show('speechPractice')}>
        <SpeechFullViewPractice sentenceList={sentenceList} voiceSpeed={voiceSpeed} isAllViewSpeechPracticeVisible={isOpen('speechPractice')} setIsAllViewSpeechPracticeVisible={vis('speechPractice')} />
      </AllView>
      <AllView style={show('partner')}>
        <LanguagePartnerView voiceSpeed={voiceSpeed} isVisible={isOpen('partner')} setIsVisible={vis('partner')} />
      </AllView>
      <AllView style={show('flashcards')}>
        <FlashcardView voiceSpeed={voiceSpeed} setIsVisible={vis('flashcards')} providedFlashcards={practiceFlashcards} onClose={() => setPracticeFlashcards(null)} />
      </AllView>
      <AllView style={show('meetings')}>
        <MeetingView voiceSpeed={voiceSpeed} setIsVisible={vis('meetings')} onPracticeMeetingContent={practiceCards} />
      </AllView>
      <AllView style={show('paragraphs')}>
        <ParagraphReaderView voiceSpeed={voiceSpeed} setIsVisible={vis('paragraphs')} onPracticeParagraphContent={practiceCards} />
      </AllView>
      <AllView style={show('media')}>
        <MediaView setIsVisible={vis('media')} />
      </AllView>
      <AllView style={show('journal')}>
        <JournalView setIsVisible={vis('journal')} voiceSpeed={voiceSpeed} isVisible={isOpen('journal')} />
      </AllView>
      <AllView style={show('manyTranslations')}>
        <ManyTranslationsFullView voiceSpeed={voiceSpeed} isAllViewChatbotVisible isManyTranslationsFullViewVisible={isOpen('manyTranslations')} setIsManyTranslationsFullViewVisible={vis('manyTranslations')} />
      </AllView>
      <AllView style={show('translation')}>
        <TranslationFullView sentenceList={sentenceList} isAllViewTranslationVisible={isOpen('translation')} setIsAllViewTranslationVisible={vis('translation')} voiceSpeed={voiceSpeed} />
      </AllView>
      {isOpen('verbLab') && <VerbFlowView standalone voiceSpeed={voiceSpeed} onClose={() => setOpenView(null)} />}
      {isOpen('sayIt') && <SayItView voiceSpeed={voiceSpeed} onClose={() => setOpenView(null)} />}
      {isOpen('song') && (
        <SongView voiceSpeed={voiceSpeed} onClose={() => setOpenView(null)}
          onSaveAsClass={(p) => { setMeetingDocPrefill(p); openDialog('dialogAddMeetingDocument') }} />
      )}
      {isOpen('specialMode') && <SpecialModeView voiceSpeed={voiceSpeed} onClose={() => setOpenView(null)} />}
      {isOpen('tree') && (
        <LanguageTreeView voiceSpeed={voiceSpeed} initialLanguage={treeFocus?.language} initialSlug={treeFocus?.slug}
          onClose={() => { setOpenView(null); setTreeFocus(null) }} />
      )}
      {openView && BARE_VIEWS.has(openView) && (
        <ViewClose onClick={() => setOpenView(null)} title="Close (Esc)"><CloseIcon /></ViewClose>
      )}

      {/* ── dialogs (portals, always mounted) ── */}
      <EditTaskDialog isOpen={dialogStates['dialogAddTask']} onClose={() => closeDialog('dialogAddTask')} task={null} />
      <AddSentenceDialog />
      <AddSentenceAllTranslationsDialog />
      <AddSentencesExcelDialog />
      <AddMeetingDocumentDialog />
      <AddLessonFromQueryDialog />
      <ImportLessonDialog />
      <ProfileDialog />
      <ReviewDueDialog onStartReview={handleStartReview} initialLang={reviewLang} />
      <IntroduceYourselfDialog onPractice={handleStartReview} />
      <HomeworkDialog voiceSpeed={voiceSpeed} />
      <AddJournalDialog />
      <AddParagraphDialog />
      <AddWordDialog />
      <AddConversationDialog />
      <AddConsumptionDialog />
      <AddProjectDialog />
      <AddTaskGroupDialog />
      <AddInspirationDialog />
      <AddNoteDialog />
      <NotesListDialog />

      {/* ── idle surfaces ── */}
      {/* Home stays mounted (its language pick and scroll survive a visit to a
          view) and is simply hidden while a view is open — every view renders
          above it, so no z-index juggling. */}
      {showHome && (
        <div style={{ display: openView ? 'none' : 'block' }}>
          <HomeView
            voiceSpeed={voiceSpeed}
            actions={actions}
            onShowCloud={() => setShowHome(false)}
            onOpenTree={openTreeAt}
            onOpenSpecialMode={() => setOpenView('specialMode')}
            onOpenVerbLab={() => setOpenView('verbLab')}
            onOpenAiLesson={dlg('dialogAddLessonFromQuery')}
            onOpenHomework={dlg('dialogHomework')}
            onOpenReview={openReviewFor}
            onOpenProfile={dlg('dialogProfile')}
          />
        </div>
      )}
      {!showHome && (
        <Hud
          sourceLanguage={sourceLanguage}
          targetLanguage={targetLanguage}
          setTargetLanguage={setTargetLanguage}
          onData={onData}
          label={label}
          setLabel={setLabel}
          owner={owner}
          setOwner={setOwner}
          labels={labels}
          owners={owners}
          languageOptions={languageOptions}
          actions={actions}
          isTeam={isTeam}
          onHome={() => setShowHome(true)}
          onToggleView={toggle}
        />
      )}
      <Global />
    </>
  )
}

// A wrapper per view. `display` is toggled rather than mounting/unmounting so
// each view keeps its state between visits. The z-index makes a stacking
// context, which is why views can't be layered *above* something at the root
// level — the shell simply hides the home page instead.
const AllView = styled.div`
  ${base}
  display: none;
  top: 0px;
  left: 0px;
  z-index: 1;
  pointer-events: auto;
`

// Close button for views that have none of their own. Above the views (1001)
// and the HUD dials (1050), below dialogs (1300+).
const ViewClose = styled.button`
  position: fixed;
  top: 1.2rem;
  right: 1.2rem;
  z-index: 1200;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 1px solid #2a2a33;
  background: rgba(20, 20, 22, 0.9);
  color: #e8e8ec;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &:hover { border-color: #6ee7b7; color: #6ee7b7; }
`

const Global = createGlobalStyle`
  * {
    box-sizing: border-box;
  }

  html,
  body,
  #root {
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 0;
    user-select: none;
    overflow: hidden;
  }

  #root {
    overflow: auto;
    padding: 0px;
  }

  body {
    position: fixed;
    overflow: hidden;
    overscroll-behavior-y: none;
    font-family: -apple-system, BlinkMacSystemFont, avenir next, avenir, helvetica neue, helvetica, ubuntu, roboto, noto, segoe ui, arial, sans-serif;
  }
`
